import { apiGet, apiPost, unwrap } from './apiClient';
import type {
  ResponseDTO,
  DeviceDetailDTO,
  DeviceSummaryDTO,
  DevicePolicySummaryDTO,
  DeviceManagedAppDTO,
  MdmCertificateInventory,
  DeviceCheckInRequest,
  BulkDeviceLockRequest,
  BulkDeviceUnenrollRequest,
} from '@/types/api';

const BASE = '/api/v1/devices';

export async function listDevices(page = 0, size = 20) {
  const res = await apiGet<ResponseDTO<DeviceSummaryDTO[]>>(
    BASE,
    { params: { page, size }, includeTenantHeader: true }
  );
  return res;
}

export async function getDeviceDetail(uuid: string) {
  const res = await apiGet<ResponseDTO<DeviceDetailDTO>>(
    `${BASE}/data/${uuid}`,
    { includeTenantHeader: true }
  );
  return unwrap(res);
}

// Flat device summary (includes user fields like username/fullName/userEmail/userId
// that aren't always present on the wrapper returned by /data/{uuid}).
export async function getDeviceSummary(uuid: string) {
  const res = await apiGet<ResponseDTO<DeviceDetailDTO>>(
    `${BASE}/uuid/${uuid}`,
    { includeTenantHeader: true }
  );
  return unwrap(res);
}

// NOTE: getDeviceApps() was removed as the same data is provided by
// getDeviceDetail()'s nested `applications` field. getDeviceManagedApps()
// below targets the dedicated managed-apps endpoint which carries install
// state (Installed / Pending / Failed / NeedsRedemption / ...).

export async function getDeviceManagedApps(deviceId: string) {
  const res = await apiGet<ResponseDTO<DeviceManagedAppDTO[]>>(
    `${BASE}/${deviceId}/managed-apps`,
    { includeTenantHeader: true }
  );
  const data = unwrap(res) as unknown;
  // Be resilient: accept a raw array, a wrapper { managedApps: [...] }, or null.
  if (Array.isArray(data)) return data as DeviceManagedAppDTO[];
  const wrapper = data as Record<string, unknown> | null;
  const list = (wrapper?.managedApps ?? wrapper?.applications ?? wrapper?.items) as
    | DeviceManagedAppDTO[]
    | undefined;
  return Array.isArray(list) ? list : [];
}

export async function getDeviceCerts(deviceId: string) {
  const res = await apiGet<ResponseDTO<MdmCertificateInventory[]>>(
    `${BASE}/${deviceId}/certificates`,
    { includeTenantHeader: true }
  );
  return unwrap(res);
}

export async function getDevicePolicies(deviceId: string, page = 0, size = 20) {
  const res = await apiGet<ResponseDTO<DevicePolicySummaryDTO[]>>(
    `${BASE}/${deviceId}/policies`,
    { params: { page, size }, includeTenantHeader: true }
  );
  return unwrap(res);
}

export async function checkInDevice(body: DeviceCheckInRequest) {
  const res = await apiPost<ResponseDTO<string>>(
    `${BASE}/check-in`,
    body,
    { includeTenantHeader: true }
  );
  return unwrap(res);
}

// NOTE: The legacy `inviteUser()` helper that POSTed to
// /api/v1/devices/invite-user has been removed. The Enroll Device flow
// now sends invitations via `sendTemplatedEmail()` in services/emailApi.ts
// (POST /api/v1/emails/send with template_type "INVITE_USER") so the
// message respects the tenant's configured SMTP settings and template.

export async function bulkLock(body: BulkDeviceLockRequest) {
  const res = await apiPost<ResponseDTO<Record<string, string>>>(
    `${BASE}/bulk-lock`,
    body,
    { includeTenantHeader: true }
  );
  return unwrap(res);
}

export async function bulkUnlock(body: { deviceIds: string[] }) {
  const res = await apiPost<ResponseDTO<Record<string, string>>>(
    `${BASE}/bulk-unlock`,
    body,
    { includeTenantHeader: true }
  );
  return unwrap(res);
}

export async function bulkUnenroll(body: BulkDeviceUnenrollRequest) {
  const res = await apiPost<ResponseDTO<Record<string, string>>>(
    `${BASE}/bulk-unenroll`,
    body,
    { includeTenantHeader: true }
  );
  return unwrap(res);
}
